import React, { useState } from 'react';
import { 
  UnofficialScoreReport, 
  MockExam 
} from '../types';
import { acknowledgeReportDisclaimer } from '../lib/mockService';
import { 
  ShieldAlert, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  HelpCircle, 
  ArrowLeft, 
  Check, 
  Sparkles,
  BarChart3,
  Clock,
  FileText,
  Lock,
  ChevronDown,
  ChevronUp,
  UserCheck,
  ThumbsUp,
  AlertCircle,
  BookOpen
} from 'lucide-react';

interface UnofficialScoreReportViewProps {
  report: UnofficialScoreReport;
  mockExam: MockExam;
  onExit: () => void;
  onUpdateReport?: (updated: UnofficialScoreReport) => void;
}

export const UnofficialScoreReportView: React.FC<UnofficialScoreReportViewProps> = ({
  report,
  mockExam,
  onExit,
  onUpdateReport
}) => {
  const [acknowledgedAt, setAcknowledgedAt] = useState<string | null>(report.disclaimerAcknowledgedAt);
  const [isAcknowledging, setIsAcknowledging] = useState<boolean>(false);
  const [expandedCRs, setExpandedCRs] = useState<Record<string, boolean>>({});

  const toggleExpandCR = (promptId: string) => {
    setExpandedCRs(prev => ({ ...prev, [promptId]: !prev[promptId] }));
  };

  const handleAcknowledge = async () => {
    setIsAcknowledging(true);
    const ts = await acknowledgeReportDisclaimer(report.id);
    setAcknowledgedAt(ts);
    setIsAcknowledging(false);
    if (onUpdateReport) {
      onUpdateReport({ ...report, disclaimerAcknowledgedAt: ts });
    }
  };

  // Readiness badge helpers
  const getReadinessBadge = (readiness: UnofficialScoreReport['estimatedReadiness']) => {
    switch (readiness) {
      case 'likely_pass':
        return {
          icon: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
          title: 'Likely Pass',
          desc: 'Your estimated performance is comfortably above the passing threshold for this exam.',
          bg: 'bg-emerald-50 border-emerald-200 text-emerald-900',
          badgeBg: 'bg-emerald-100 text-emerald-800'
        };
      case 'borderline':
        return {
          icon: <AlertTriangle className="w-5 h-5 text-amber-600" />,
          title: 'Borderline Range',
          desc: 'Your estimated score is within 10 points of the passing score. Targeted review of weak domains is strongly recommended before your test date.',
          bg: 'bg-amber-50 border-amber-200 text-amber-900',
          badgeBg: 'bg-amber-100 text-amber-800'
        };
      case 'likely_not_pass':
        return {
          icon: <XCircle className="w-5 h-5 text-rose-600" />,
          title: 'Likely Not Pass Yet',
          desc: 'Your estimated score is more than 10 points below the passing score. Focus your study on the domains with lower accuracy indicated below.',
          bg: 'bg-rose-50 border-rose-200 text-rose-900',
          badgeBg: 'bg-rose-100 text-rose-800'
        };
      case 'unavailable':
      default:
        return {
          icon: <HelpCircle className="w-5 h-5 text-slate-500" />,
          title: 'Scale Estimate Unavailable',
          desc: "This exam's official scoring scale hasn't been independently confirmed yet, so we can't generate a reliable score estimate. Your raw score and domain breakdown above are still accurate and useful for identifying where to focus your review.",
          bg: 'bg-slate-50 border-slate-200 text-slate-800',
          badgeBg: 'bg-slate-200 text-slate-700'
        };
    }
  };

  const isUnlocked = Boolean(acknowledgedAt);
  const readinessInfo = getReadinessBadge(report.estimatedReadiness);

  return (
    <div id="unofficial-score-report-root" className="max-w-4xl mx-auto my-8 px-4 space-y-8 animate-in fade-in pb-16">
      {/* Top Header Card */}
      <div id="report-header" className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-extrabold bg-indigo-50 text-indigo-700 border border-indigo-100 mb-2">
            <BarChart3 className="w-3.5 h-3.5" /> Unofficial Practice Score Report
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            {mockExam.seriesLabel} Practice Evaluation
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Exam ID: <span className="font-mono text-slate-700">{mockExam.paperId}</span> • Attempt: <span className="font-mono text-slate-700 text-xs">{report.mockAttemptId}</span>
          </p>
        </div>

        <button
          id="btn-return-directory-top"
          onClick={onExit}
          className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition flex items-center gap-2 shrink-0"
        >
          <ArrowLeft className="w-4 h-4" /> Return to Directory
        </button>
      </div>

      {/* STEP 3 DISCLAIMER GATE */}
      {!isUnlocked ? (
        <div id="disclaimer-gate-card" className="bg-gradient-to-b from-amber-50/70 to-white rounded-3xl border-2 border-amber-200 shadow-xl p-8 sm:p-12 text-center space-y-6 animate-in zoom-in-95">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center mx-auto border-2 border-amber-300 shadow-sm">
            <ShieldAlert className="w-8 h-8" />
          </div>

          <div className="max-w-xl mx-auto space-y-3">
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">
              Before Viewing Your Practice Evaluation
            </h2>
            <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm text-left">
              <p className="text-sm text-slate-700 leading-relaxed font-medium">
                &ldquo;This is your estimated practice score, not your official result. Real
                exam scoring uses methods we can&apos;t fully replicate -- so treat this as a
                signal, not a guarantee. If you&apos;re in the &apos;Borderline&apos; or &apos;Likely Not
                Pass Yet&apos; range, that&apos;s useful information now, while you still have time
                to review -- not something to find out on exam day.&rdquo;
              </p>
            </div>
          </div>

          <div className="pt-2">
            <button
              id="btn-acknowledge-disclaimer"
              onClick={handleAcknowledge}
              disabled={isAcknowledging}
              className="px-8 py-3.5 rounded-2xl bg-slate-900 hover:bg-black text-white text-sm font-black shadow-lg hover:shadow-xl transition transform active:scale-98 flex items-center gap-2.5 mx-auto"
            >
              {isAcknowledging ? (
                'Unlocking Report...'
              ) : (
                <>
                  <Check className="w-4 h-4 text-emerald-400" /> I understand, show my results
                </>
              )}
            </button>
            <p className="text-[11px] text-slate-400 mt-2">
              Requires explicit confirmation before score analysis is revealed
            </p>
          </div>
        </div>
      ) : (
        /* STEP 4 UNLOCKED FULL REPORT */
        <div id="full-report-content" className="space-y-8 animate-in fade-in duration-300">
          
          {/* Top Score Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Raw Multiple Choice Score */}
            <div id="card-raw-score" className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Multiple-Choice Raw Score</span>
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
                  Exact Match
                </span>
              </div>
              <div className="flex items-baseline gap-3">
                <div className="text-5xl font-black text-slate-900 tracking-tight">
                  {report.rawScore.correct} <span className="text-2xl font-normal text-slate-400">/ {report.rawScore.total}</span>
                </div>
                <div className="text-lg font-bold text-indigo-600">
                  {report.rawScore.percentCorrect}%
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Number of multiple-choice questions correctly answered out of total questions presented.
              </p>
            </div>

            {/* Estimated Scaled Score & Readiness Band */}
            <div id="card-estimated-score" className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Estimated Score</span>
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${readinessInfo.badgeBg}`}>
                  {readinessInfo.title}
                </span>
              </div>

              {report.estimatedScaledScore !== null ? (
                <div>
                  <div className="flex items-baseline gap-3">
                    <div className="text-5xl font-black text-slate-900 tracking-tight">
                      {report.estimatedScaledScore}
                    </div>
                    {report.scoreScaleMin !== null && report.scoreScaleMax !== null && (
                      <span className="text-xs font-medium text-slate-400">
                        (Scale: {report.scoreScaleMin} – {report.scoreScaleMax})
                      </span>
                    )}
                  </div>
                  {report.realPassingScore !== null && (
                    <div className="text-xs font-semibold text-slate-600 mt-1">
                      Passing Threshold Target: <span className="text-slate-900 font-bold">{report.realPassingScore}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-slate-600 font-medium py-2">
                  Estimated Score Unavailable
                </div>
              )}

              <p className="text-xs text-slate-500">
                {readinessInfo.desc}
              </p>
            </div>

          </div>

          {/* Domain-by-Domain Accuracy Breakdown */}
          <div id="section-domain-breakdown" className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
            <div>
              <h3 className="text-lg font-black text-slate-900">Domain-by-Domain Accuracy</h3>
              <p className="text-xs text-slate-500">
                Detailed breakdown of multiple-choice performance across standardized competency domains.
              </p>
            </div>

            <div className="space-y-4">
              {report.domainBreakdown.map((domain, idx) => {
                const pct = domain.percentCorrect;
                let barColor = 'bg-rose-500';
                if (pct >= 75) barColor = 'bg-emerald-500';
                else if (pct >= 60) barColor = 'bg-amber-500';

                return (
                  <div key={idx} className="bg-slate-50/80 rounded-2xl p-4 border border-slate-100 space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                      <span className="text-sm font-bold text-slate-900">{domain.domainName}</span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="font-semibold text-slate-600">
                          {domain.correct} / {domain.total} correct
                        </span>
                        <span className="font-black text-slate-900 bg-white px-2 py-0.5 rounded-md border border-slate-200">
                          {domain.percentCorrect}%
                        </span>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Constructed Response Scoring & Rubric Criteria Checklist (Phase 6) */}
          {report.constructedResponseEstimates && report.constructedResponseEstimates.length > 0 && (
            <div id="section-constructed-responses" className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6 shadow-sm">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pb-2 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center border border-indigo-100">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-slate-900">Constructed-Response & Essay Evaluations</h3>
                    <p className="text-xs text-slate-500">
                      {report.constructedResponseEstimates.length} written item(s) evaluated against paper-specific scoring rubrics.
                    </p>
                  </div>
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold bg-amber-50 text-amber-900 border border-amber-200">
                  <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                  AI-Assisted Evaluation
                </div>
              </div>

              <div className="space-y-6">
                {report.constructedResponseEstimates.map((cr, idx) => {
                  const isExpanded = expandedCRs[cr.promptId] ?? true;
                  const confidenceBadge = cr.confidence === 'high' 
                    ? { bg: 'bg-emerald-50 text-emerald-800 border-emerald-200', text: 'High AI Confidence' }
                    : cr.confidence === 'medium'
                    ? { bg: 'bg-amber-50 text-amber-800 border-amber-200', text: 'Medium AI Confidence' }
                    : { bg: 'bg-rose-50 text-rose-800 border-rose-200', text: 'Low AI Confidence' };

                  return (
                    <div 
                      key={cr.promptId || idx} 
                      id={`cr-estimate-card-${idx}`}
                      className="bg-slate-50/80 rounded-2xl border border-slate-200 overflow-hidden space-y-4 p-5 sm:p-6"
                    >
                      {/* CR Item Top Bar */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-black text-slate-900 uppercase tracking-wide">
                              Item #{idx + 1}
                            </span>
                            {cr.domain && (
                              <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                                {cr.domain}
                              </span>
                            )}
                            <code className="text-[11px] font-mono text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                              {cr.promptId}
                            </code>
                          </div>
                        </div>

                        {/* Direct Adjacent AI-Estimated Label */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span 
                            id={`badge-ai-disclaimer-${idx}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-amber-100 text-amber-950 border border-amber-300 shadow-xs"
                          >
                            <ShieldAlert className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                            AI-estimated — not an official score
                          </span>

                          {cr.confidence && (
                            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${confidenceBadge.bg}`}>
                              {confidenceBadge.text}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Prompt and Student Response View */}
                      {cr.promptText && (
                        <div className="bg-white rounded-xl p-4 border border-slate-200 text-xs space-y-1.5 shadow-2xs">
                          <span className="font-bold text-slate-700 uppercase tracking-wider text-[10px] block">
                            Prompt Question
                          </span>
                          <p className="text-slate-800 font-medium leading-relaxed">
                            {cr.promptText}
                          </p>
                        </div>
                      )}

                      {/* Student Submitted Response */}
                      {cr.responseText && (
                        <div className="bg-slate-100/80 rounded-xl p-4 border border-slate-200/80 text-xs space-y-1.5">
                          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px] block">
                            Your Submitted Response
                          </span>
                          <p className="text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                            {cr.responseText}
                          </p>
                        </div>
                      )}

                      {/* Score Display Card */}
                      <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                            Estimated Rubric Score
                          </div>
                          <div className="flex items-baseline gap-2 mt-1">
                            {cr.aiEstimatedScore !== null ? (
                              <>
                                <span className="text-4xl font-black text-slate-900 tracking-tight">
                                  {cr.aiEstimatedScore}
                                </span>
                                {cr.scaleMax !== undefined && (
                                  <span className="text-lg font-bold text-slate-400">
                                    / {cr.scaleMax}
                                  </span>
                                )}
                                <span className="text-xs font-semibold text-slate-500 ml-2">
                                  ({cr.rubricScale})
                                </span>
                              </>
                            ) : (
                              <span className="text-sm font-bold text-slate-600">
                                Evaluation Pending
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Human Review Recommendation Banner */}
                        {cr.humanReviewRecommended && (
                          <div 
                            id={`human-review-banner-${idx}`}
                            className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-rose-900 text-xs flex items-start gap-2.5 max-w-md shadow-2xs"
                          >
                            <UserCheck className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                            <div>
                              <span className="font-bold block text-rose-950">
                                Human Review Recommended
                              </span>
                              <p className="text-[11px] text-rose-800 leading-snug mt-0.5">
                                This score falls in the lower rubric tiers or has moderate rater certainty. We recommend having a certified educator or peer review your draft before test day.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Rubric Justification */}
                      {cr.aiJustification && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200 space-y-2">
                          <span className="text-xs font-black text-slate-900 flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-indigo-600" />
                            Rubric-Grounded Scoring Justification
                          </span>
                          <p className="text-xs text-slate-700 leading-relaxed">
                            {cr.aiJustification}
                          </p>
                        </div>
                      )}

                      {/* Student-Facing Checklist: Strengths & Weaknesses */}
                      {((cr.strengths && cr.strengths.length > 0) || (cr.weaknesses && cr.weaknesses.length > 0) || cr.actionableFeedback) && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200 space-y-4">
                          <div className="text-xs font-black text-slate-900 uppercase tracking-wider">
                            Rubric Criteria Checklist
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Strengths Checklist */}
                            {cr.strengths && cr.strengths.length > 0 && (
                              <div className="bg-emerald-50/50 rounded-xl p-3.5 border border-emerald-100 space-y-2">
                                <span className="text-xs font-bold text-emerald-900 flex items-center gap-1.5">
                                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                                  Observed Strengths (Criteria Met)
                                </span>
                                <ul className="space-y-1.5">
                                  {cr.strengths.map((st, sIdx) => (
                                    <li key={sIdx} className="text-xs text-emerald-800 flex items-start gap-2">
                                      <span className="text-emerald-500 font-bold shrink-0">•</span>
                                      <span>{st}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Weaknesses Checklist */}
                            {cr.weaknesses && cr.weaknesses.length > 0 && (
                              <div className="bg-amber-50/50 rounded-xl p-3.5 border border-amber-100 space-y-2">
                                <span className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                                  <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                                  Areas for Improvement (Criteria Missing)
                                </span>
                                <ul className="space-y-1.5">
                                  {cr.weaknesses.map((wk, wIdx) => (
                                    <li key={wIdx} className="text-xs text-amber-800 flex items-start gap-2">
                                      <span className="text-amber-500 font-bold shrink-0">•</span>
                                      <span>{wk}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>

                          {/* Actionable Feedback */}
                          {cr.actionableFeedback && (
                            <div className="bg-indigo-50/60 rounded-xl p-3.5 border border-indigo-100 text-xs text-indigo-950 space-y-1">
                              <span className="font-bold flex items-center gap-1.5 text-indigo-900">
                                <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                                Next Steps to Reach Top Score Tier:
                              </span>
                              <p className="text-indigo-900/90 leading-relaxed pl-5">
                                {cr.actionableFeedback}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Official Rubric Text Accordion */}
                      {cr.rubricNotes && (
                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => toggleExpandCR(cr.promptId)}
                            className="text-xs font-bold text-slate-500 hover:text-slate-800 flex items-center gap-1.5 transition"
                          >
                            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            {isExpanded ? 'Hide Official Scoring Rubric' : 'View Official Scoring Rubric for this Prompt'}
                          </button>
                          {isExpanded && (
                            <div className="mt-2 p-3.5 bg-slate-100/90 rounded-xl border border-slate-200 text-xs text-slate-600 font-mono leading-relaxed whitespace-pre-wrap">
                              {cr.rubricNotes}
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action Footer */}
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-slate-200">
            <div className="text-xs text-slate-400 text-center sm:text-left">
              Disclaimer acknowledged on {new Date(acknowledgedAt).toLocaleString()}
            </div>
            <button
              id="btn-return-directory-bottom"
              onClick={onExit}
              className="px-6 py-3 rounded-2xl bg-slate-900 hover:bg-black text-white text-xs font-bold transition flex items-center gap-2 shadow-md"
            >
              <ArrowLeft className="w-4 h-4" /> Return to Exam Directory
            </button>
          </div>

        </div>
      )}
    </div>
  );
};
